/**
 * Company service
 * Handles Firestore Companies collection operations
 */

import { convertDocumentTimestamps } from "@shared/utils/firestore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type { Company } from "../../../shared/types";
import { db, functions } from "../config/firebase";

export const getCompanyById = async (
  companyId: string,
): Promise<Company | null> => {
  const snap = await getDoc(doc(db, "Companies", companyId));
  if (!snap.exists()) {
    return null;
  }
  return {
    id: snap.id,
    ...convertDocumentTimestamps<Company>(snap.data()),
  } as Company;
};

/**
 * Get all active (non-archived) companies ordered by creation date
 * Uses server-side filtering for efficiency
 */
export const getCompanies = async (): Promise<Company[]> => {
  const companiesQuery = query(
    collection(db, "Companies"),
    where("isArchived", "==", false),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(companiesQuery);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...convertDocumentTimestamps<Company>(doc.data()),
  })) as Company[];
};

/**
 * Get all archived companies ordered by creation date
 * Uses server-side filtering for efficiency
 */
export const getArchivedCompanies = async (): Promise<Company[]> => {
  const companiesQuery = query(
    collection(db, "Companies"),
    where("isArchived", "==", true),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(companiesQuery);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...convertDocumentTimestamps<Company>(doc.data()),
  })) as Company[];
};

/**
 * Update company settings (maxUsers)
 * Uses Cloud Function for authorization
 */
export const updateCompanySettings = async (
  companyId: string,
  maxUsers: number,
): Promise<void> => {
  const updateSettings = httpsCallable(functions, "updateCompanySettings");
  await updateSettings({ companyId, maxUsers });
};

/**
 * Update company payroll settings (grace period, overtime policy)
 * Company admin or super_admin only
 */
export const updatePayrollSettings = async (
  companyId: string,
  settings: {
    gracePeriodMinutes: number;
    timezone?: string;
  },
): Promise<void> => {
  const fn = httpsCallable(functions, "updatePayrollSettings");
  await fn({ companyId, ...settings });
};

/**
 * Archive a company (soft delete)
 * Moves company to archive bin - can be restored
 * Uses Cloud Function for authorization
 */
export const archiveCompany = async (companyId: string): Promise<void> => {
  const archive = httpsCallable(functions, "archiveCompany");
  await archive({ companyId });
};

/**
 * Restore a company from archive
 * Uses Cloud Function for authorization
 */
export const restoreCompany = async (companyId: string): Promise<void> => {
  const restore = httpsCallable(functions, "restoreCompany");
  await restore({ companyId });
};

/**
 * Permanently delete a company and all related data
 * This is a destructive operation that cannot be undone
 * Uses Cloud Function for authorization and cascade deletion
 *
 * Deletes:
 * - Company document
 * - All users (staff, managers, administrator) - both Firestore profiles and Firebase Auth accounts
 * - All job assignments
 * - All time entries
 * - All messages involving company users
 */
export const permanentlyDeleteCompany = async (
  companyId: string,
): Promise<void> => {
  const deleteCompany = httpsCallable(functions, "permanentlyDeleteCompany");
  await deleteCompany({ companyId });
};
